import { useState } from 'react';
import { Modal } from '../components/Modal';
import { api, ApiError } from '../lib/api';
import type { InvoiceStatus } from '../hooks/useTelegram';

type Props = {
    onClose: () => void;
    /** Called after the deposit either confirms (status='paid') or fails. */
    onResult: (info: { status: 'paid' | 'failed' | 'pending' | 'cancelled'; amount: number; method: 'stars' | 'cryptobot' }) => void;
};

/**
 * Top-up dialog. Creates a real Stars / CryptoBot invoice via the backend and
 * hands the link to Telegram.WebApp.openInvoice (Stars) or openLink
 * (CryptoBot web invoice). Crediting happens server-side on the webhook —
 * the UI just listens for the openInvoice callback to know if the flow ended
 * in success, cancel, or failure.
 */
export function TopUpModal({ onClose, onResult }: Props) {
    const [amount, setAmount] = useState('');
    const [source, setSource] = useState<'stars' | 'cryptobot'>('stars');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const [coupon, setCoupon] = useState('');
    const [couponBusy, setCouponBusy] = useState(false);
    const [couponMsg, setCouponMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    const redeemCoupon = async () => {
        const code = coupon.trim();
        if (!code || couponBusy) return;
        setCouponBusy(true); setCouponMsg(null);
        try {
            const r = await api.redeemCoupon(code);
            setCouponMsg({ kind: 'ok', text: r.message });
            setCoupon('');
        } catch (e) {
            const msg = e instanceof ApiError
                ? (e.code === 'not_found' ? 'Такого купона нет.'
                 : e.code === 'expired' ? 'Купон истёк.'
                 : e.code === 'exhausted' ? 'Купон уже исчерпан.'
                 : e.code === 'disabled' ? 'Купон отключён.'
                 : `${e.status}: ${e.code}`)
                : 'Не удалось активировать.';
            setCouponMsg({ kind: 'err', text: msg });
        } finally { setCouponBusy(false); }
    };

    const amountNum = Number.parseInt(amount.replace(',', '.'), 10);
    const valid = Number.isFinite(amountNum) && amountNum > 0;

    const submit = async () => {
        if (!valid || busy) return;
        setBusy(true);
        setErr(null);
        try {
            const r = await api.createDepositInvoice({ amount: amountNum, method: source });
            if (source === 'stars') {
                const wa = window.Telegram?.WebApp;
                if (wa?.openInvoice) {
                    wa.openInvoice(r.link, (status: InvoiceStatus) => {
                        onResult({ status, amount: amountNum, method: 'stars' });
                    });
                } else {
                    window.open(r.link, '_blank', 'noopener');
                    onResult({ status: 'pending', amount: amountNum, method: 'stars' });
                }
            } else {
                const wa = window.Telegram?.WebApp;
                const url = r.link;
                if (wa?.openLink) wa.openLink(url);
                else window.open(url, '_blank', 'noopener');
                onResult({ status: 'pending', amount: amountNum, method: 'cryptobot' });
            }
        } catch (e) {
            const msg = e instanceof ApiError && e.code === 'payments_unavailable'
                ? 'Платежи временно недоступны — попробуй позже.'
                : (e instanceof ApiError ? `${e.status}: ${e.code}` : 'Не удалось создать счёт.');
            setErr(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal title="Пополнить баланс" onClose={onClose}>
            <div className="input-group">
                <label>Способ оплаты</label>
                <div className="method-pick">
                    <button
                        type="button"
                        className={`method-btn ${source === 'stars' ? 'active' : ''}`}
                        onClick={() => setSource('stars')}
                    >
                        <span className="method-icon">⭐</span>
                        <span>Stars</span>
                    </button>
                    <button
                        type="button"
                        className={`method-btn ${source === 'cryptobot' ? 'active' : ''}`}
                        onClick={() => setSource('cryptobot')}
                    >
                        <span className="method-icon">🤖</span>
                        <span>CryptoBot</span>
                    </button>
                </div>
            </div>

            <div className="input-group">
                <label>Сумма BSP</label>
                <input
                    type="number"
                    inputMode="numeric"
                    placeholder="100"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                />
                <span className="hint">
                    {source === 'stars'
                        ? 'Оплата ⭐ Stars: 1 BSP = 1 ⭐. Telegram откроет окно оплаты.'
                        : 'Оплата через CryptoBot. Откроется веб-страница для оплаты.'}
                </span>
            </div>

            {err && <p className="popup-text" style={{ color: 'var(--red)' }}>{err}</p>}

            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!valid || busy}
                onClick={submit}
            >
                {busy ? '...' : 'Перейти к оплате'}
            </button>

            <div className="input-group" style={{ marginTop: 14 }}>
                <label>Купон / промокод</label>
                <div style={{ display: 'flex', gap: 6 }}>
                    <input
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                        placeholder="WELCOME100"
                        style={{ flex: 1, textTransform: 'uppercase' }}
                    />
                    <button
                        type="button"
                        className="action-btn btn-outline-gold"
                        style={{ width: 'auto', padding: '0 14px' }}
                        disabled={!coupon.trim() || couponBusy}
                        onClick={redeemCoupon}
                    >
                        {couponBusy ? '…' : 'OK'}
                    </button>
                </div>
                {couponMsg && (
                    <span
                        className="hint"
                        style={{ color: couponMsg.kind === 'ok' ? 'var(--green)' : 'var(--red)' }}
                    >
                        {couponMsg.text}
                    </span>
                )}
            </div>
        </Modal>
    );
}
